import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase/firebaseConfig';
import axiosInstance, { getApiBaseUrl } from './axiosInstance';

const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isInitialLoginRedirect, setIsInitialLoginRedirect] = useState(true);

    const navigate = useNavigate();
    const location = useLocation();
    const firestore = getFirestore();

    // Fetch user data from Firestore
    const fetchUserData = async (currentUser) => {
        try {
            let userDoc = doc(firestore, 'users', currentUser.uid);
            let docSnap = await getDoc(userDoc);

            if (!docSnap.exists()) {
                userDoc = doc(firestore, 'onboarding', currentUser.uid);
                docSnap = await getDoc(userDoc);
            }

            if (docSnap.exists()) {
                const userData = docSnap.data();
                const userRole = userData.role?.toLowerCase();
                if (userRole) {
                    localStorage.setItem('user_role', userRole);
                    return userRole;
                }
            }
        } catch (error) {
            console.error('Error fetching user data from Firestore:', error.message);
        }
        return null;
    };

    // Get user role
    const getUserRole = useCallback(async () => {
        if (user) {
            const userRole = localStorage.getItem('user_role');
            if (userRole) {
                return userRole;
            } else {
                return await fetchUserData(user);
            }
        }
        return null;
    }, [user]);

    // Get user email
    const getUserEmail = useCallback(() => {
        return user?.email || null;
    }, [user]);

    // Get user UUID
    const getUserUUID = useCallback(() => {
        return user?.uid || null;
    }, [user]);

    // Get user token
    const getUserToken = useCallback(async () => {
        if (user) {
            try {
                return await user.getIdToken();
            } catch (error) {
                console.error('Error getting user token:', error.message);
                throw new Error('Unable to retrieve token');
            }
        } else {
            return null;
        }
    }, [user]);

    // Check if user is authenticated
    const isAuthenticated = useCallback(() => {
        return user !== null;
    }, [user]);

    // Listen for auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setLoading(true);
            setUser(currentUser);

            if (currentUser) {
                const userRole = await getUserRole();
                if (userRole) {
                    localStorage.setItem('user_role', userRole);
                    if (isInitialLoginRedirect && !['/signin', '/signup'].includes(location.pathname)) {
                        setIsInitialLoginRedirect(false);
                        navigate(`/${userRole}/dashboard`, { replace: true });
                    }
                }
            } else {
                // Gracefully handle logout or no user found
                localStorage.removeItem('user_role');
                localStorage.removeItem('access_token');
                setIsInitialLoginRedirect(true);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate, getUserRole, isInitialLoginRedirect, location.pathname]);

    // Signup function
    const signup = useCallback(async (email, password) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            return userCredential;
        } catch (error) {
            console.error('Signup error:', error.message);
            throw new Error(error.message);
        }
    }, []);

    // Login function
    const login = useCallback(async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            if (!user) throw new Error('Login failed: No user returned');

            const idToken = await user.getIdToken();
            const response = await axiosInstance.post('/api/auth/login', { idToken });

            if (response.status === 200) {
                const { access_token } = response.data;
                localStorage.setItem('access_token', access_token);

                const userRole = await fetchUserData(user);
                if (userRole) {
                    setIsInitialLoginRedirect(true);
                    navigate(`/${userRole}/dashboard`, { replace: true });
                }

                return userCredential;
            } else {
                throw new Error('Unexpected response status');
            }
        } catch (error) {
            console.error('Login error:', error.message);
            throw new Error(error.message);
        }
    }, [navigate]);

    // Logout function
    const logout = useCallback(async () => {
        try {
            await signOut(auth);
            setUser(null);
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_role');
            navigate('/signin', { replace: true });
        } catch (error) {
            console.error('Logout error:', error.message);
            throw new Error('Unable to sign out');
        }
    }, [navigate]);

    const value = {
        user,
        signup,
        login,
        logout,
        getUserToken,
        getUserUUID,
        getUserRole,
        getUserEmail,
        isAuthenticated,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading ? children : <div style={{ textAlign: 'center', paddingTop: '2rem' }}>Loading user session...</div>}
        </AuthContext.Provider>
    );
};
